import { Injectable } from "@angular/core";
import { EntityInfoHelper } from "@app/entity/model/entityinfo";
import { EntityInfoDbService } from "@app/entity/services/entityinfo-db.service";
import { EntityInfoService } from "@app/entity/services/entityinfo.service";
import * as EntityInfoActions from "@app/entity/store/actions/entityinfo.actions";
import { EffectsUtils } from "@app/root-store/effects/effect-creators";
import { DeleteConfirmationService } from "@app/shared/components/delete-confirmation/delete-confirmation.service";
import { ActionWord } from "@app/shared/resources/action-word";
import { Actions } from "@ngrx/effects";
import { combineLatest, filter, map, of, take } from "rxjs";
import { Observable } from "rxjs/internal/Observable";

interface DeleteEntityInfoPayload {
    payload: EntityInfoActions.ActionPayload;
    display: string;
}
@Injectable()
export class EntityInfoEffects extends EffectsUtils {
    constructor(
        actions$: Actions,
        private entityinfoDbService: EntityInfoDbService,
        private readonly deleteConfirmationService: DeleteConfirmationService,
        private readonly entityInfoService: EntityInfoService
    ) {
        super(actions$);
    }

    getAllEntityInfo$ = this.createSwitchEffect({
        dbMethod: ({ entityId }) =>
            combineLatest({
                entityInfos: this.entityinfoDbService.getAll(entityId),
                infoTypes: this.entityInfoService.selectEntityInfoTypes().pipe(
                    filter(types => !!types),
                    take(1),
                    map(typesObject => Object.values(typesObject))
                ),
            }).pipe(
                map(({ entityInfos, infoTypes }) => {
                    entityInfos.forEach(info => {
                        const infoType = infoTypes.find(t => t.id === info.entityinfo_type_id);
                        info["type_name"] = infoType;
                    });
                    return entityInfos;
                }),
                map(data =>
                    data.reduce((items, data) => {
                        items[data.id] = EntityInfoHelper.fromDbObject(data);
                        return items;
                    }, {})
                ),
                map(entityinfos => ({ entityinfos }))
            ),
        actionSet: EntityInfoActions.loadSet,
    });

    createEntityInfo$ = this.createExhaustEffect({
        dbMethod: (payload: EntityInfoActions.ActionPayload) => this.entityinfoDbService.create(payload.entityinfo, payload.logData),
        actionSet: EntityInfoActions.createSet,
    });

    createEntityInfoSuccess$ = this.createSuccessEffect(EntityInfoActions.createSet.success, ActionWord.created);
    createEntityInfoFailure$ = this.createFailureEffect(EntityInfoActions.createSet.failure, ActionWord.created);

    deleteEntityInfoConfirmation$ = this.createDeleteConfirmationEffect({
        actionSet: EntityInfoActions.deleteConfirmationSet,
        dbMethod: (payload: EntityInfoActions.ActionPayload): Observable<DeleteEntityInfoPayload> =>
            of({ payload, display: `this ${payload.entityinfo?.type?.name}` }),
        confirmationService: this.deleteConfirmationService,
    });

    deleteEntityInfoSilent$ = this.createExhaustEffect({
        dbMethod: (payload: EntityInfoActions.ActionPayload) => this.entityinfoDbService.delete(payload.entityinfo, payload.logData),
        actionSet: EntityInfoActions.silentDeleteSet,
    });

    deleteEntityInfo$ = this.createSwitchEffect({
        dbMethod: (payload: EntityInfoActions.ActionPayload) => this.entityinfoDbService.delete(payload.entityinfo, payload.logData),
        actionSet: EntityInfoActions.deleteSet,
    });

    deleteEntityInfoSuccess$ = this.createSuccessEffect(EntityInfoActions.deleteSet.success, ActionWord.deleted);
    deleteEntityInfoFailure$ = this.createFailureEffect(EntityInfoActions.deleteSet.failure, ActionWord.deleted);

    updateEntityInfo$ = this.createExhaustEffect({
        dbMethod: (payload: EntityInfoActions.ActionPayload) => this.entityinfoDbService.update(payload.entityinfo, payload.logData),
        actionSet: EntityInfoActions.updateSet,
    });

    updateEntityInfoSuccess$ = this.createSuccessEffect(EntityInfoActions.updateSet.success, ActionWord.saved);
    updateEntityInfoFailure$ = this.createFailureEffect(EntityInfoActions.updateSet.failure, ActionWord.saved);
}
